import React, { useState } from "react";
import {
    useGetPaymentIncomeListQuery,
    useGetPaymentIncomeDetailsQuery,
} from "../api";
import { formatCurrency } from "../utils/formatters";

const PAYMENT_METHODS = [
    { value: "", label: "Все методы" },
    { value: "BEP20", label: "BEP20" },
    { value: "TRC20", label: "TRC20" },
    { value: "TON", label: "TON" },
    { value: "CryptoBot", label: "CryptoBot" },
    { value: "Stripe", label: "Stripe" },
    { value: "Altyn", label: "Altyn" },
    { value: "FriendlyPay", label: "FriendlyPay" },
];

const SOURCE_TABLES = [
    { value: "", label: "Все источники" },
    { value: "alny_russia_payment", label: "Russia Payment" },
    { value: "alny_payment_cryptobot", label: "CryptoBot" },
    { value: "alny_payments_stripe", label: "Stripe" },
    { value: "altyn_payments", label: "Altyn" },
    { value: "friendly_pay_payments", label: "FriendlyPay" },
];

const STATUSES = [
    { value: "", label: "Все статусы" },
    { value: "paid", label: "Оплачено" },
    { value: "pending", label: "В ожидании" },
    { value: "cancelled", label: "Отменено" },
    { value: "failed", label: "Неудачно" },
];

const SOURCES = [
    { value: "", label: "Все источники" },
    { value: "hub", label: "Хаб" },
    { value: "game", label: "Игра" },
];

export const PaymentIncomePage: React.FC = () => {
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState<"payment_time" | "amount" | "created_at" | "user_id">("payment_time");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [userId, setUserId] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [status, setStatus] = useState("");
    const [sourceTable, setSourceTable] = useState("");
    const [source, setSource] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);

    const { data: paymentsResponse, isLoading, refetch } = useGetPaymentIncomeListQuery({
        page,
        per_page: 15,
        sort_by: sortBy,
        sort_order: sortOrder,
        user_id: userId ? parseInt(userId) : undefined,
        payment_method: paymentMethod || undefined,
        status: status || undefined,
        source_table: sourceTable || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        source: source || undefined,
    });

    const { data: paymentDetails, isLoading: isLoadingDetails } =
        useGetPaymentIncomeDetailsQuery(selectedPaymentId!, {
            skip: !selectedPaymentId,
        });

    const payments = paymentsResponse?.data || [];
    const meta = paymentsResponse?.meta;

    const handleSort = (field: "payment_time" | "amount" | "created_at" | "user_id") => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("desc");
        }
        setPage(1);
    };

    const handleFilterChange = () => {
        setPage(1);
    };

    const handleViewDetails = (paymentId: number) => {
        setSelectedPaymentId(paymentId);
    };

    const handleCloseModal = () => {
        setSelectedPaymentId(null);
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString("ru-RU");
    };

    const renderSourceData = (sourceData: Record<string, any>) => {
        return Object.entries(sourceData).map(([key, value]) => (
            <div key={key} className="mb-2">
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                    {key}:
                </span>{" "}
                <span className="text-gray-900 dark:text-white">
                    {value === null || value === undefined ? "N/A" : String(value)}
                </span>
            </div>
        ));
    };

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Пополнения
            </h1>

            {/* Фильтры */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            ID пользователя
                        </label>
                        <input
                            type="number"
                            value={userId}
                            onChange={(e) => {
                                setUserId(e.target.value);
                                handleFilterChange();
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="ID пользователя"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Метод оплаты
                        </label>
                        <select
                            value={paymentMethod}
                            onChange={(e) => {
                                setPaymentMethod(e.target.value);
                                handleFilterChange();
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            {PAYMENT_METHODS.map((method) => (
                                <option key={method.value} value={method.value}>
                                    {method.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Источник
                        </label>
                        <select
                            value={source}
                            onChange={(e) => {
                                setSource(e.target.value);
                                handleFilterChange();
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            {SOURCES.map((s) => (
                                <option key={s.value} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Статус
                        </label>
                        <select
                            value={status}
                            onChange={(e) => {
                                setStatus(e.target.value);
                                handleFilterChange();
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            {STATUSES.map((s) => (
                                <option key={s.value} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Таблица
                        </label>
                        <select
                            value={sourceTable}
                            onChange={(e) => {
                                setSourceTable(e.target.value);
                                handleFilterChange();
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            {SOURCE_TABLES.map((source) => (
                                <option key={source.value} value={source.value}>
                                    {source.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Дата от
                        </label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => {
                                setDateFrom(e.target.value);
                                handleFilterChange();
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Дата до
                        </label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => {
                                setDateTo(e.target.value);
                                handleFilterChange();
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    </div>
                </div>
            </div>

            {/* Таблица */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            ) : payments.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    Платежи не найдены
                </div>
            ) : (
                <>
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                            onClick={() => handleSort("user_id")}
                                        >
                                            ID
                                            {sortBy === "user_id" && (
                                                <span className="ml-1">
                                                    {sortOrder === "asc" ? "↑" : "↓"}
                                                </span>
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Пользователь
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Источник
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                            onClick={() => handleSort("amount")}
                                        >
                                            Сумма
                                            {sortBy === "amount" && (
                                                <span className="ml-1">
                                                    {sortOrder === "asc" ? "↑" : "↓"}
                                                </span>
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Метод
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Таблица
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Статус
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                            onClick={() => handleSort("payment_time")}
                                        >
                                            Дата платежа
                                            {sortBy === "payment_time" && (
                                                <span className="ml-1">
                                                    {sortOrder === "asc" ? "↑" : "↓"}
                                                </span>
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Действия
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {payments.map((payment) => (
                                        <tr
                                            key={payment.id}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {payment.user_id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {payment.user_name} {payment.user_surname}
                                                <br />
                                                <span className="text-xs text-gray-500">
                                                    {payment.user_email}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {payment.source}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                                {formatCurrency(payment.amount)} {payment.currency}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {payment.payment_method}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {payment.source_table}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <span
                                                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        payment.status === "paid"
                                                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                                            : payment.status === "pending"
                                                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                                            : payment.status === "cancelled"
                                                            ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                                            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                                    }`}
                                                >
                                                    {payment.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {formatDate(payment.payment_time)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => handleViewDetails(payment.id)}
                                                    className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                                                >
                                                    Подробнее
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Пагинация */}
                        {meta && (
                            <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                                <div className="flex-1 flex justify-between sm:hidden">
                                    <button
                                        onClick={() => setPage(page - 1)}
                                        disabled={page === 1}
                                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                                    >
                                        Назад
                                    </button>
                                    <button
                                        onClick={() => setPage(page + 1)}
                                        disabled={page >= meta.last_page}
                                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                                    >
                                        Вперед
                                    </button>
                                </div>
                                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm text-gray-700 dark:text-gray-300">
                                            Показано <span className="font-medium">{meta.from}</span> до{" "}
                                            <span className="font-medium">{meta.to}</span> из{" "}
                                            <span className="font-medium">{meta.total}</span> результатов
                                        </p>
                                    </div>
                                    <div>
                                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                            <button
                                                onClick={() => setPage(page - 1)}
                                                disabled={page === 1}
                                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                                            >
                                                Назад
                                            </button>
                                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Страница {meta.current_page} из {meta.last_page}
                                            </span>
                                            <button
                                                onClick={() => setPage(page + 1)}
                                                disabled={page >= meta.last_page}
                                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                                            >
                                                Вперед
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Модалка с деталями */}
            {selectedPaymentId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Детали платежа
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                            >
                                ✕
                            </button>
                        </div>

                        {isLoadingDetails ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : paymentDetails?.data ? (
                            <div className="space-y-6">
                                <div>
                                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                                        Основная информация
                                    </h4>
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                        {renderSourceData(paymentDetails.data.payment_income)}
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                                        Данные из исходной таблицы ({paymentDetails.data.payment_income.source_table})
                                    </h4>
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                        {renderSourceData(paymentDetails.data.source_data)}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                                Ошибка загрузки данных
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

